import type { FC, PropsWithChildren } from 'react'
import { clsx } from 'clsx'
import { CorrectoLogo } from '../CorrectoLogo/CorrectoLogo'
import { Icon } from '../Icon/Icon'
import { Typography } from '../Typography/Typography'

// Define the MenuItemProps interface
interface MenuItemProps {
  icon: React.ReactNode
  className?: string
  title: string
  active: boolean
  onClick?: () => void
}

// Create the MenuItem component
export const MenuItem: FC<MenuItemProps> = ({
  icon,
  title,
  active,
  onClick,
  className,
}) => {
  const menuItemClasses = clsx(
    'transition duration-400 ease-in-out rounded-lg text-blue-correcto-700 flex items-center p-4 cursor-pointer w-full',
    {
      'bg-neutral-100': active,
      'hover:bg-neutral-100 active:bg-neutral-100': !active,
    },
    className,
  )

  return (
    <button className={menuItemClasses} onClick={onClick} type="button">
      {icon}
      <Typography className="ml-2 font-semibold" variant="body2">
        {title}
      </Typography>
    </button>
  )
}

type SidebarProps = PropsWithChildren<{
  isPremium?: boolean
  logoutHandler: () => void
  logoutText?: string
}>

export const Sidebar: FC<SidebarProps> = ({
  children,
  logoutHandler,
  logoutText = 'Logout',
  isPremium = false,
}) => {
  return (
    <div className="sidebar h-full overflow-x-scroll w-[300px] min-w-[300px] bg-gray-100">
      <div className="flex flex-col h-full px-6 py-8">
        <div className="box-content flex items-center">
          <CorrectoLogo variant="long-blue" />
          {isPremium ? (
            <div className="text-orange-generative-500 rounded-[16px] bg-white items-center justify-center ml-2 p-1 w-8 h-8">
              <Icon icon="crown" size="md" />
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-2 pt-10 pb-8">{children}</div>
        <div className="mt-auto">
          <MenuItem
            active={false}
            icon={<Icon icon="logout" size="md" />}
            onClick={logoutHandler}
            title={logoutText}
          />
        </div>
      </div>
    </div>
  )
}
